// Match Lineup — incident symbol set (design handoff "Match Lineup
// Component.dc.html", buildIcon()/ballParts()/incidentIcons(), ported 1:1).
//
// ONE source of truth: pitch nodes, bench rows, unavailable rows and
// substitution cards all draw from this set, so a symbol change propagates
// everywhere at once. Direction of a substitution is carried by the arrow and
// its colour — slate out / blue in — never by the circle. Card colours are
// the universal yellow/red; nothing else in the component is red or green.

import React from "react";
import { isSamePerson } from "./lineupUtils";

// ── provider incident type → symbol key ────────────────────────────────────
//
// EXPLICIT mapping of the provider's lineup-incident `type` codes (TheSports
// "STATUS CODE → technical statistics" table) to the named symbol set below.
// Codes the table carries but a lineup never shows (corners, offsides, match
// phases, possession…) are DELIBERATELY absent: an unknown or unmapped code
// renders NOTHING (never a fallback glyph) and is logged once per code.
//
//   provider code  meaning (provider table)   symbol key
//   ─────────────  ─────────────────────────  ─────────────────────────────
//   1              Goal                       'goal'    (or 'assist' — see
//                                             resolveIncidentType: the same
//                                             goal incident in the ASSISTING
//                                             player's list renders his 'A')
//   3              Yellow card                'yellow'
//   4              Red card                   'red'
//   8              Penalty (scored)           'pengoal'
//   9              Substitution               'in' / 'out' (direction from
//                                             in_player / out_player vs the
//                                             player the list belongs to)
//   15             Second yellow → red        'yellowred'
//   16             Penalty missed             'penmiss'
//   17             Own goal                   'owngoal'
export const INCIDENT_TYPE_MAP = {
  1: "goal",
  3: "yellow",
  4: "red",
  8: "pengoal",
  9: "substitution",
  15: "yellowred",
  16: "penmiss",
  17: "owngoal",
};

const warnedCodes = {};

// Resolve ONE provider incident to a symbol key for the player whose list it
// sits in. Unknown codes -> null + one console.warn per code (brief §0.3).
export const resolveIncidentType = (incident, player) => {
  const code = Number(incident && incident.type);
  const mapped = INCIDENT_TYPE_MAP[code];
  if (!mapped) {
    if (!Number.isNaN(code) && !warnedCodes[code]) {
      warnedCodes[code] = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[MatchLineup] unknown incident type code ${code} — rendered nothing`
      );
    }
    return null;
  }
  // Person references (player / in_player / out_player / assist1 / assist2)
  // arrive as `{id, name}` OBJECTS in the real persisted rows (QA 2026-08-17,
  // live staging ingest) — plain-string names are the fixture/legacy variant.
  // isSamePerson (lineupUtils) matches id-first with a name fallback.
  if (mapped === "substitution") {
    if (isSamePerson(incident.in_player, player)) return "in";
    if (isSamePerson(incident.out_player, player)) return "out";
    return null; // a sub incident naming neither side renders nothing
  }
  if (mapped === "goal") {
    // Real incident keys (§0-audit): type, time, minute, addtime, belong,
    // home_score, away_score, player, in_player, out_player, assist1
    // (+assist2). The same goal incident can sit in more than one player's
    // list: in the ASSISTING player's list (referenced as assist1/assist2) it
    // renders his 'A'; in the scorer's list (incident.player = him, or no
    // subject at all) the ball; a goal that names a DIFFERENT scorer and
    // does not credit this player renders nothing.
    if (
      isSamePerson(incident.assist1, player) ||
      isSamePerson(incident.assist2, player)
    ) {
      return "assist";
    }
    if (incident.player && !isSamePerson(incident.player, player)) return null;
    return "goal";
  }
  return mapped;
};

// All symbol keys of one player's incident list, in provider order.
export const playerGlyphKeys = (player) =>
  ((player && player.incidents) || [])
    .map((inc) => resolveIncidentType(inc, player))
    .filter(Boolean);

// ── SVG set (handoff buildIcon, verbatim geometry) ─────────────────────────

// Football in a 32×32 space: dark sphere, five light panels.
const BALL_PANELS = [
  "M16 10 L16 3.5 L23.76 5.32 L27.89 12.14 L21.71 14.15 Z",
  "M21.71 14.15 L27.89 12.14 L28.55 20.08 L23.35 26.11 L19.53 20.85 Z",
  "M19.53 20.85 L23.35 26.11 L16 29.2 L8.65 26.11 L12.47 20.85 Z",
  "M12.47 20.85 L8.65 26.11 L3.44 20.08 L4.11 12.14 L10.29 14.15 Z",
  "M10.29 14.15 L4.11 12.14 L8.24 5.32 L16 3.5 L16 10 Z",
];

const ballParts = (dark, light, stroke) => (
  <>
    <circle cx={16} cy={16} r={15} fill={dark} stroke={stroke} strokeWidth={1.2} />
    {BALL_PANELS.map((d, i) => (
      <path key={`p${i}`} d={d} fill={light} />
    ))}
  </>
);

const FONT = "Helvetica Neue, Helvetica, Arial, sans-serif";
const blockStyle = { display: "block", flex: "none" };

// The 13-symbol set. `size` is the box edge, `onDark` switches the sub-arrow
// palette for the navy pitch (slate reads as "out" on white rows; on the dark
// pitch it needs a light slate) and gives the arrows their dark backing disc.
export const IncidentSymbol = ({ type, size = 13, onDark = false }) => {
  const s = size;
  const outColor = onDark ? "#E2E8F0" : "#64748B";
  const inColor = onDark ? "#6FB4FF" : "#2183FE";
  const box = (kids, w, h) => (
    <svg
      width={w || s}
      height={h || s}
      viewBox="0 0 16 16"
      fill="none"
      style={blockStyle}
    >
      {kids}
    </svg>
  );
  switch (type) {
    case "goal":
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none" style={blockStyle}>
          {ballParts("#0F172A", "#F1F1F1", "#0F172A")}
        </svg>
      );
    case "owngoal":
      return (
        <svg
          width={s}
          height={Math.round(s * 1.3)}
          viewBox="0 0 32 42"
          fill="none"
          style={blockStyle}
        >
          <g>{ballParts("#EF4444", "#FFFFFF", "#B91C1C")}</g>
          <path d="M2 37 H26" stroke="#EF4444" strokeWidth={2.4} strokeLinecap="round" />
          <path
            d="M23 32.6 L29 37 L23 41.4"
            stroke="#EF4444"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      );
    case "pengoal":
      return box(
        <>
          <circle cx={8} cy={8} r={6.4} fill="#F8FAFC" stroke="#0F172A" strokeWidth={1.2} />
          <text x={8} y={11} textAnchor="middle" fontSize={8} fontWeight={800} fill="#0F172A" fontFamily={FONT}>
            P
          </text>
        </>
      );
    case "penmiss":
      return box(
        <>
          <circle cx={8} cy={8} r={6.4} fill="#FFFFFF" stroke="#64748B" strokeWidth={1.2} />
          <text x={8} y={11} textAnchor="middle" fontSize={8} fontWeight={800} fill="#94A3B8" fontFamily={FONT}>
            P
          </text>
          <path d="M3.4 12.6 L12.6 3.4" stroke="#334155" strokeWidth={1.4} strokeLinecap="round" />
        </>
      );
    case "assist":
      return box(
        <>
          <circle cx={8} cy={8} r={6.4} fill="#2183FE" />
          <text x={8} y={11} textAnchor="middle" fontSize={8} fontWeight={800} fill="#FFFFFF" fontFamily={FONT}>
            A
          </text>
        </>
      );
    case "yellow":
      return box(
        <rect x={4.6} y={2.2} width={6.8} height={11.6} rx={1.4} fill="#FACC15" stroke="#CA8A04" strokeWidth={0.7} />
      );
    case "red":
      return box(
        <rect x={4.6} y={2.2} width={6.8} height={11.6} rx={1.4} fill="#EF4444" stroke="#B91C1C" strokeWidth={0.7} />
      );
    case "yellowred":
      return box(
        <>
          <rect x={2.2} y={2.6} width={6.2} height={10.8} rx={1.3} fill="#FACC15" stroke="#CA8A04" strokeWidth={0.7} />
          <rect x={7.4} y={2.6} width={6.2} height={10.8} rx={1.3} fill="#EF4444" stroke="#B91C1C" strokeWidth={0.7} />
        </>
      );
    case "in":
      return box(
        <>
          <circle cx={8} cy={8} r={7.4} fill={onDark ? "rgba(15,23,42,0.72)" : "transparent"} />
          <path d="M8 13.2 L8 3.6" stroke={inColor} strokeWidth={1.9} strokeLinecap="round" />
          <path
            d="M4.2 7.2 L8 3.2 L11.8 7.2"
            stroke={inColor}
            strokeWidth={1.9}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </>
      );
    case "out":
      return box(
        <>
          <circle cx={8} cy={8} r={7.4} fill={onDark ? "rgba(15,23,42,0.72)" : "transparent"} />
          <path d="M8 2.8 L8 12.4" stroke={outColor} strokeWidth={1.9} strokeLinecap="round" />
          <path
            d="M4.2 8.8 L8 12.8 L11.8 8.8"
            stroke={outColor}
            strokeWidth={1.9}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </>
      );
    case "injured":
      return box(
        <path
          d="M6.4 2.6 h3.2 v3.8 h3.8 v3.2 h-3.8 v3.8 h-3.2 v-3.8 h-3.8 v-3.2 h3.8 z"
          fill="#EF4444"
        />,
        12,
        12
      );
    case "suspended":
      return box(
        <rect x={4.6} y={2.2} width={6.8} height={11.6} rx={1.4} fill="#EF4444" stroke="#B91C1C" strokeWidth={0.7} />
      );
    case "questionable":
      return box(
        <>
          <circle cx={8} cy={8} r={6.4} fill="none" stroke="#64748B" strokeWidth={1.4} />
          <text x={8} y={11.4} textAnchor="middle" fontSize={9} fontWeight={800} fill="#475569" fontFamily={FONT}>
            ?
          </text>
        </>,
        12,
        12
      );
    default:
      return null;
  }
};

// ── ×n stacking (handoff incidentIcons, render half) ───────────────────────

// One grouped entry: the glyph, plus a ×N count chip when the same type
// repeats (a brace of goals, two assists…).
export const GroupedIncidentSymbol = ({ type, count = 1, size = 13, onDark = false }) => {
  if (count < 2) return <IncidentSymbol type={type} size={size} onDark={onDark} />;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <span style={{ display: "flex" }}>
        <IncidentSymbol type={type} size={size} onDark={onDark} />
      </span>
      <span
        style={{
          fontSize: `${Math.max(9, Math.round(size * 0.72))}px`,
          fontWeight: 800,
          lineHeight: 1,
          color: "#FFFFFF",
          background: "rgba(15,23,42,0.88)",
          borderRadius: 3,
          padding: "1px 3px",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count}
      </span>
    </span>
  );
};

export default IncidentSymbol;
